import { Modal, Space, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import type { TableRowActionItem } from '~/app/components/common/TableRowActionsMenu/types';
import type { DiagnosticsHistoryRow } from '../types';
import {
  diagnosticsDisplayFileName,
  diagnosticsIsCancellable,
  diagnosticsShouldShowOcppStatus,
  diagnosticsStatusTagColor,
  diagnosticsUploadedWithoutFile,
  formatDiagnosticsRequestedAt,
} from '../utils';
import styles from '../DiagnosticsHistoryPanel.module.css';

type Options = {
  downloadingId: string | null;
  cancellingId: string | null;
  unavailableById: Record<string, string>;
  onDownload: (row: DiagnosticsHistoryRow) => void;
  onCancel: (row: DiagnosticsHistoryRow) => void;
};

export function useDiagnosticsHistoryColumns(
  options: Options,
): ColumnsType<DiagnosticsHistoryRow> {
  const { t } = useTranslation('assets');
  const { downloadingId, cancellingId, unavailableById, onDownload, onCancel } =
    options;

  return useMemo(
    () => [
      {
        title: t('detail.commands.diagnosticsHistory.columns.status'),
        dataIndex: 'status',
        key: 'status',
        width: 150,
        render: (status: string, row) => (
          <Space direction="vertical" size={2}>
            <Tag
              className={styles.statusTag}
              color={diagnosticsStatusTagColor(status)}
            >
              {status}
            </Tag>
            {diagnosticsShouldShowOcppStatus(row) && row.ocppStatus ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('detail.commands.diagnosticsHistory.ocppStatus', {
                  status: row.ocppStatus,
                })}
              </Typography.Text>
            ) : null}
            {diagnosticsUploadedWithoutFile(row) ? (
              <Tag className={styles.statusTag} color="warning">
                {t('detail.commands.diagnosticsHistory.uploadedWithoutFile')}
              </Tag>
            ) : null}
          </Space>
        ),
      },
      {
        title: t('detail.commands.diagnosticsHistory.columns.requestedAt'),
        dataIndex: 'requestedAt',
        key: 'requestedAt',
        width: 180,
        render: (value: string) => formatDiagnosticsRequestedAt(value),
      },
      {
        title: t('detail.commands.diagnosticsHistory.columns.fileName'),
        key: 'fileName',
        ellipsis: true,
        render: (_: unknown, row) =>
          diagnosticsDisplayFileName(row) ??
          t('detail.commands.diagnosticsHistory.fileNameUnknown'),
      },
      {
        title: t('detail.commands.diagnosticsHistory.columns.actions'),
        key: 'actions',
        width: 56,
        align: 'center' as const,
        render: (_: unknown, row) => {
          const unavailableKey = unavailableById[row.id];
          if (unavailableKey) {
            return (
              <Typography.Text className={styles.unavailableText}>
                {t(unavailableKey)}
              </Typography.Text>
            );
          }

          const items: TableRowActionItem[] = [];

          if (diagnosticsIsCancellable(row)) {
            items.push({
              key: 'cancel',
              label: t('detail.commands.diagnosticsHistory.cancel'),
              danger: true,
              onClick: () => {
                Modal.confirm({
                  title: t(
                    'detail.commands.diagnosticsHistory.cancelConfirmTitle',
                  ),
                  content: t(
                    'detail.commands.diagnosticsHistory.cancelConfirmContent',
                  ),
                  okText: t(
                    'detail.commands.diagnosticsHistory.cancelConfirmOk',
                  ),
                  cancelText: t('detail.commands.confirmCancel'),
                  okButtonProps: { danger: true },
                  onOk: () => onCancel(row),
                });
              },
            });
          }

          if (row.downloadable) {
            items.push({
              key: 'download',
              label: t('detail.commands.diagnosticsHistory.download'),
              onClick: () => onDownload(row),
            });
          }

          if (items.length === 0) {
            return (
              <Typography.Text className={styles.unavailableText}>
                {t('detail.commands.diagnosticsHistory.downloadUnavailable')}
              </Typography.Text>
            );
          }

          return (
            <TableRowActionsMenu
              loading={cancellingId === row.id || downloadingId === row.id}
              items={items}
            />
          );
        },
      },
    ],
    [cancellingId, downloadingId, onCancel, onDownload, t, unavailableById],
  );
}
